<template>
  <div
    class="relative flex-col flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full px-4 py-0 border-t-0 border-b-0 border-l-2 border-r-0 border-transparent border-solid cursor-pointer conversation hover:bg-slate-25 dark:hover:bg-slate-800 group"
    :class="{
      'active bg-slate-25 dark:bg-slate-800 border-woot-500': isActiveChat,
      'unread-chat': hasUnread || manualUnread,
      'has-inbox-name': showInboxName,
      'conversation-selected': selected,
    }"
    @mouseenter="onCardHover"
    @mouseleave="onCardLeave"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <div class="flex">
      <button
        v-if="isAnEmailChannel"
        class="self-center"
        @click="toggleSubsconversation"
      >
        <fluent-icon
          v-if="!subconversationOpened"
          icon="arrow-chevron2-right"
        />
        <fluent-icon v-else icon="arrow-chevron-down" />
      </button>
      <label v-if="hovered || selected" class="checkbox-wrapper" @click.stop>
        <input
          :value="selected"
          :checked="selected"
          class="checkbox"
          type="checkbox"
          @change="onSelectConversation($event.target.checked)"
        />
      </label>
      <thumbnail
        v-if="bulkActionCheck"
        :src="currentContact.thumbnail"
        :badge="inboxBadge"
        class="columns"
        :username="currentContact.name"
        :status="currentContact.availability_status"
        size="40px"
      />
      <div
        class="px-0 py-3 border-b group-last:border-transparent group-hover:border-transparent border-slate-50 dark:border-slate-800/75 columns"
      >
        <div class="flex justify-between">
          <inbox-name v-if="showInboxName" :inbox="inbox" />
          <div class="flex gap-2 ml-2 rtl:mr-2 rtl:ml-0">
            <span v-if="pinned">
              <fluent-icon
                icon="pin"
                size="14"
                class="text-slate-500 dark:text-slate-400"
              />
            </span>
            <span
              v-if="showAssignee && assignee.name"
              class="text-slate-500 dark:text-slate-400 text-xs font-medium leading-3 py-0.5 px-0 inline-flex text-ellipsis overflow-hidden whitespace-nowrap"
            >
              <fluent-icon
                icon="person"
                size="12"
                class="text-slate-500 dark:text-slate-400"
              />
              {{ assignee.name }}
            </span>
            <priority-mark :priority="chat.priority" />
          </div>
        </div>
        <h4
          class="conversation--user text-sm my-0 mx-2 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap w-[60%] text-slate-900 dark:text-slate-100"
        >
          {{ currentContact.name }}
        </h4>
        <message-preview
          v-if="lastMessageInChat"
          :message="lastMessageInChat"
          :additional-attributes="chat.additional_attributes"
          class="conversation--message my-0 mx-2 leading-6 h-6 max-w-[96%] w-[16.875rem] text-sm text-slate-700 dark:text-slate-200"
        />
        <p
          v-else
          class="conversation--message text-slate-700 dark:text-slate-200 text-sm my-0 mx-2 leading-6 h-6 max-w-[96%] w-[16.875rem] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          <fluent-icon
            size="16"
            class="-mt-0.5 align-middle inline-block text-slate-600 dark:text-slate-300"
            icon="info"
          />
          <span>
            {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
          </span>
        </p>
        <div class="absolute flex flex-col conversation--meta right-4 top-4">
          <span
            dir="ltr"
            class="ml-auto font-normal leading-4 text-black-600 text-xxs"
          >
            {{ messageStampFull(chat.timestamp) }}
          </span>
          <div class="mode-noread-container">
            <img
              v-if="isBot"
              class="mode-icon"
              src="~dashboard/assets/images/robot.png"
            />
            <img
              v-else-if="isSnoozed"
              class="mode-icon"
              src="~dashboard/assets/images/sand_clock.png"
            />
            <img
              v-else
              class="mode-icon"
              src="~dashboard/assets/images/customer-support.png"
            />
            <span
              class="unread shadow-lg rounded-full hidden text-xxs font-semibold h-4 leading-4 ml-auto mt-1 min-w-[1rem] px-1 py-0 text-center text-white bg-green-400"
            >
              {{ manualUnread ? '' : unreadCount > 9 ? '9+' : unreadCount }}
            </span>
          </div>
        </div>
        <card-labels :conversation-id="chat.id" class="mt-0.5 mx-2 mb-0">
          <template v-if="isDisplaySlaLabel" #before>
            <SLA-card-label :chat="chat" class="ltr:mr-1 rtl:ml-1" />
          </template>
        </card-labels>
      </div>
      <woot-context-menu
        v-if="!hideContextMenu && showContextMenu"
        ref="menu"
        :x="contextMenu.x"
        :y="contextMenu.y"
        @close="closeContextMenu"
      >
        <conversation-context-menu
          :status="chat.status"
          :inbox-id="inbox.id"
          :priority="chat.priority"
          :has-unread-messages="hasUnread"
          :conversation-id="chat.id"
          :pinned="pinned"
          @update-conversation="onUpdateConversation"
          @assign-agent="onAssignAgent"
          @assign-label="onAssignLabel"
          @assign-team="onAssignTeam"
          @mark-as-unread="markAsUnread"
          @assign-priority="assignPriority"
          @pin="onPin"
        />
      </woot-context-menu>
    </div>
    <div v-if="subconversationOpened" class="flex flex-col w-full">
      <div
        v-for="message in chatMessages"
        :key="message.id"
        class="mx-2 py-3 relative border-b group-last:border-transparent border-slate-300 dark:border-slate-800/75 columns"
        :class="{
          active: isActive(message),
        }"
        @click.prevent.stop="handlePreviewClick(message)"
      >
        <div class="absolute flex flex-col conversation--meta right-4 top-4">
          <span
            dir="ltr"
            class="ml-auto font-normal leading-4 text-black-600 text-xxs"
          >
            {{ messageStampFull(message.created_at) }}
          </span>
        </div>
        <h4
          class="conversation--user text-sm my-0 mx-2 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap w-[60%] text-slate-900 dark:text-slate-100"
        >
          {{ message.sender.name }}
          <br />
          <span
            v-if="message.sender && message.sender.email"
            class="text-xs lowercase"
          >
            {{ '<' + message.sender.email + '>' }}
          </span>
        </h4>
        <email-message-preview
          :message="message"
          :additional-attributes="chat.additional_attributes"
          :enable-delete="true"
          class="conversation--message my-0 mx-2 leading-6 h-6 max-w-[96%] w-[16.875rem] text-sm text-slate-700 dark:text-slate-200"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import Thumbnail from '../Thumbnail.vue';
import MessagePreview from './MessagePreview.vue';
import EmailMessagePreview from './EmailMessagePreview.vue';
import conversationMixin from '../../../mixins/conversations';
import timeMixin from '../../../mixins/time';
import router from '../../../routes';
import { frontendURL, conversationUrl } from '../../../helper/URLHelper';
import InboxName from '../InboxName.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import wootConstants from '../../../constants/globals';
import ConversationContextMenu from './contextMenu/Index.vue';
import alertMixin from 'shared/mixins/alertMixin';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import localeMixin from 'shared/mixins/localeMixin';
import workingHoursMixin from 'shared/mixins/workingHoursMixin';
import PriorityMark from './PriorityMark.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  components: {
    CardLabels,
    InboxName,
    Thumbnail,
    ConversationContextMenu,
    MessagePreview,
    PriorityMark,
    SLACardLabel,
    EmailMessagePreview,
  },

  mixins: [
    inboxMixin,
    timeMixin,
    conversationMixin,
    alertMixin,
    localeMixin,
    workingHoursMixin /* d99d */,
  ],
  props: {
    pinned: {
      type: Boolean,
      default: false,
    },
    activeLabel: {
      type: String,
      default: '',
    },
    chat: {
      type: Object,
      default: () => {},
    },
    showInboxNameAlways: {
      type: Boolean,
      default: false,
    },
    hideThumbnail: {
      type: Boolean,
      default: false,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showAssignee: {
      type: Boolean,
      default: false,
    },
    conversationType: {
      type: String,
      default: '',
    },
    selected: {
      type: Boolean,
      default: false,
    },
    hideContextMenu: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeSubconversationId: null,
      openSubconversation: false,
      hovered: false,
      showContextMenu: false,
      contextMenu: {
        x: null,
        y: null,
      },
      recentlyMarkedAsUnread: false,
    };
  },
  computed: {
    subconversationOpened() {
      return this.isActiveChat && this.openSubconversation;
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxesList: 'inboxes/getInboxes',
      activeInbox: 'getSelectedInbox',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      itemMarkedAsUnread: 'getItemMarkedAsUnread',
    }),
    chatMessages() {
      return this.chat.messages
        .filter(message => {
          return !message.private && message.sender;
        })
        .sort((a, b) => b.created_at - a.created_at);
    },
    bulkActionCheck() {
      return !this.hideThumbnail && !this.hovered && !this.selected;
    },
    chatMetadata() {
      return this.chat.meta || {};
    },

    assignee() {
      return this.chatMetadata.assignee || {};
    },

    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chatMetadata.sender.id
      );
    },

    isActiveChat() {
      return this.currentChat.id === this.chat.id;
    },

    unreadCount() {
      return this.chat.unread_count;
    },

    hasUnread() {
      return this.unreadCount > 0 || this.recentlyMarkedAsUnread;
    },

    manualUnread() {
      return this.recentlyMarkedAsUnread || this.isManualUnread(this.chat);
    },

    isInboxNameVisible() {
      return !this.activeInbox;
    },

    lastMessageInChat() {
      return this.lastMessage(this.chat);
    },

    inbox() {
      const { inbox_id: inboxId } = this.chat;
      const stateInbox = this.$store.getters['inboxes/getInbox'](inboxId);
      return stateInbox;
    },

    showInboxName() {
      return (
        (this.showInboxNameAlways || // d99d
          this.isInboxNameVisible) &&
        this.inboxesList.length > 1
      );
    },
    inboxName() {
      const stateInbox = this.inbox;
      return stateInbox.name || '';
    },
    hasSlaPolicyId() {
      return this.chat?.sla_policy_id;
    },
    // todo copypast
    isOpen() {
      return this.chat.status === wootConstants.STATUS_TYPE.OPEN;
    },
    isResolved() {
      return this.chat.status === wootConstants.STATUS_TYPE.RESOLVED;
    },
    isBot() {
      return this.isOpen || this.isResolved;
    },
    isSnoozed() {
      return this.chat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    appliedSLA() {
      return this.chat?.applied_sla;
    },
    isSlaWorkingHours() {
      return (
        !this.appliedSLA?.sla_only_during_business_hours ||
        this.isInBusinessHours
      );
    },
    isDisplaySlaLabel() {
      return !this.isResolved && this.hasSlaPolicyId && this.isSlaWorkingHours;
    },
  },
  watch: {
    isActiveChat(newState) {
      if (newState === true) {
        this.recentlyMarkedAsUnread = false;

        if (this.itemMarkedAsUnread === this.chat.id) {
          this.$store.dispatch('setItemMarkedAsUnread', null);
        }
      }
    },
    itemMarkedAsUnread(newVal) {
      if (newVal === this.chat.id) {
        this.recentlyMarkedAsUnread = true;
      }
    },
  },
  methods: {
    isActive(message) {
      return this.isActiveChat && this.activeSubconversationId == message.id;
    },
    async handlePreviewClick(message) {
      if (message.id === this.activeSubconversationId) return;
      bus.$emit(BUS_EVENTS.CLOSE_REPLY);
      this.activeSubconversationId = message.id;
      await this.$store.dispatch('showBefore', {
        conversationId: this.currentChat.id,
        before: message.id,
      });
    },
    toggleSubsconversation() {
      this.openSubconversation = !this.openSubconversation;
    },
    onCardClick(e) {
      const { activeInbox, chat } = this;
      const path = frontendURL(
        conversationUrl({
          accountId: this.accountId,
          activeInbox,
          id: chat.id,
          label: this.activeLabel,
          teamId: this.teamId,
          foldersId: this.foldersId,
          conversationType: this.conversationType,
        })
      );

      if (e.metaKey || e.ctrlKey) {
        window.open(
          window.chatwootConfig.hostURL + path,
          '_blank',
          'noopener noreferrer nofollow'
        );
        return;
      }
      if (this.isActiveChat) {
        this.$store.dispatch('clearBeforeConversation');
        return;
      }

      router.push({ path });
    },
    onCardHover() {
      this.hovered = !this.hideThumbnail;
    },
    onCardLeave() {
      this.hovered = false;
    },
    onSelectConversation(checked) {
      const action = checked ? 'select-conversation' : 'de-select-conversation';
      this.$emit(action, this.chat.id, this.inbox.id);
    },
    openContextMenu(e) {
      e.preventDefault();
      this.$emit('context-menu-toggle', true);
      this.contextMenu.x = e.pageX || e.clientX;
      this.contextMenu.y = e.pageY || e.clientY;
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.$emit('context-menu-toggle', false);
      this.showContextMenu = false;
      this.contextMenu.x = null;
      this.contextMenu.y = null;
    },
    onUpdateConversation(status, snoozedUntil) {
      this.$emit(
        'update-conversation-status',
        this.chat.id,
        status,
        snoozedUntil
      );
    },
    async onAssignAgent(agent) {
      this.$emit('assign-agent', agent, [this.chat.id]);
    },
    async onAssignLabel(label) {
      this.$emit('assign-label', [label.title], [this.chat.id]);
    },
    async onAssignTeam(team) {
      this.$emit('assign-team', team, this.chat.id);
    },
    async markAsUnread() {
      this.$emit('mark-as-unread', this.chat.id);
    },
    async assignPriority(priority) {
      this.$emit('assign-priority', priority, this.chat.id);
    },
    async onPin() {
      this.$emit('pin', this.chat.id);
    },
  },
};
</script>
<style lang="scss" scoped>
.conversation {
  &:hover {
    // d99d
    background: var(--color-background-middle);
  }

  &.unread-chat {
    .unread {
      @apply block;
    }
    .conversation--message {
      @apply font-semibold;
    }
    .conversation--user {
      @apply font-semibold;
    }
  }

  &.compact {
    @apply pl-0;
    .conversation--details {
      @apply rounded-sm ml-0 pl-5 pr-2;
    }
  }

  &::v-deep .user-thumbnail-box {
    @apply mt-4;
  }

  &.conversation-selected {
    @apply bg-slate-25 dark:bg-slate-800;
  }

  &.has-inbox-name {
    &::v-deep .user-thumbnail-box {
      @apply mt-8;
    }
    .checkbox-wrapper {
      @apply mt-8;
    }
    .conversation--meta {
      @apply mt-4;
    }
  }

  .checkbox-wrapper {
    @apply h-10 w-10 flex items-center justify-center rounded-full cursor-pointer mt-4 hover:bg-woot-100 dark:hover:bg-woot-800;

    input[type='checkbox'] {
      @apply m-0 cursor-pointer;
    }
  }
  .active {
    @apply bg-woot-75;
  }
}

// d99d
.mode-icon {
  height: 24px;
  width: 24px;
  margin-inline-start: var(--space-small);
  margin-inline-end: var(--space-small);
}
.mode-noread-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 5rem;
  padding-inline-start: 16px;
}

[dir='rtl'] .conversation--metadata {
  padding-right: unset;
  padding-left: var(--space-normal);
}
// .
</style>
